import type { Map } from "mapbox-gl"

interface TransitLayerOptions {
	baseUrl: string
	sourceName: string
	layerName: string
	visibleLayers: string[]
}

export function addTransitLines(map: Map, { baseUrl, visibleLayers, layerName, sourceName }: TransitLayerOptions) {
	map.addSource(sourceName, {
		type: "vector",
		tiles: [`${baseUrl}/core_public.transit_lines/{z}/{x}/{y}.pbf`],
	})
	map.addLayer({
		id: layerName,
		source: sourceName,
		"source-layer": "core_public.transit_lines",
		type: "line",
		layout: {
			visibility: visibleLayers.includes("transit-lines") ? "visible" : "none",
			"line-join": "round",
			"line-cap": "round",
		},
		paint: {
			// prettier-ignore
			"line-color": [
				"case",
				["all", ["==", ["get", "mode"], "metro"], ["==", ["get", "name"], "Ligne Orange"]], "rgb(245,121,33)",
				["all", ["==", ["get", "mode"], "metro"], ["==", ["get", "name"], "Ligne Bleue"]], "rgb(0,114,206)",
				["all", ["==", ["get", "mode"], "metro"], ["==", ["get", "name"], "Ligne Verte"]], "rgb(0,150,57)",
				["all", ["==", ["get", "mode"], "metro"], ["==", ["get", "name"], "Ligne Jaune"]], "rgb(255,209,0)",
				["all", ["==", ["get", "mode"], "metro"], ["==", ["get", "name"], "Ligne Rose"]], "rgb(255,105,180)",
				["==", ["get", "mode"], "rem"], "rgb(126,174,0)",
				["==", ["get", "mode"], "tram"], "rgb(116,127,129)",
				["==", ["get", "mode"], "srb"], "rgb(1,171,132)",
				["==", ["get", "mode"], "exo"], "rgb(2,129,153)",
				"rgb(15,98,254)"
			],
			// prettier-ignore
			"line-opacity": [
				"case",
				["==", ["get", "status"], "in_use"], 1,
				["==", ["get", "status"], "in_construction"], 150 / 255,
				["==", ["get", "status"], "proposed"], 100 / 255,
				1
			],
			// prettier-ignore
			"line-dasharray": [
				"match",
				["get", "status"],
				"in_construction", ["literal", [1.2, 1.4]],
				"proposed", ["literal", [1.2, 1.4]],
				["literal", [1, 0]]
			],
			"line-width": [
				"interpolate",
				["linear"],
				["zoom"],

				10,
				["match", ["get", "mode"], "metro", 3, "srb", 1.5, "tram", 1.5, "rem", 2, "exo", 1.5, 1.5],

				14,
				["match", ["get", "mode"], "metro", 6, "srb", 3, "tram", 3, "rem", 4, "exo", 3, 3],

				18,
				["match", ["get", "mode"], "metro", 12, "srb", 6, "tram", 6, "rem", 8, "exo", 6, 6],
			],
		},
		minzoom: 7,
		metadata: { custom: true },
	})
}

export function addTransitStations(map: Map, { baseUrl, visibleLayers, layerName, sourceName }: TransitLayerOptions) {
	map.addSource(sourceName, {
		type: "vector",
		tiles: [`${baseUrl}/core_public.transit_stations/{z}/{x}/{y}.pbf`],
	})
	map.addLayer({
		id: layerName,
		source: sourceName,
		"source-layer": "core_public.transit_stations",
		type: "circle",
		layout: {
			visibility: visibleLayers.includes("transit-stations") ? "visible" : "none",
		},
		paint: {
			"circle-color": "#ffffff",
			"circle-stroke-color": "#303030",
			"circle-stroke-width": 1.5,
			"circle-radius": ["interpolate", ["linear"], ["zoom"], 7, 1.5, 10, 2.5, 13, 3.5],
			"circle-opacity": 0.95,
		},
		minzoom: 7,
		metadata: { custom: true },
	})
}

export function addTransitStationsLabels(
	map: Map,
	{ baseUrl, visibleLayers, layerName, sourceName }: TransitLayerOptions
) {
	map.addSource(sourceName, {
		type: "vector",
		tiles: [`${baseUrl}/core_public.transit_stations/{z}/{x}/{y}.pbf`],
	})
	map.addLayer({
		id: layerName,
		source: sourceName,
		"source-layer": "core_public.transit_stations",
		type: "symbol",
		layout: {
			"text-field": ["coalesce", ["to-string", ["get", "name"]], ""],
			"text-font": ["Open Sans Bold", "Arial Unicode MS Regular"],
			"text-size": ["interpolate", ["linear"], ["zoom"], 13, 10, 17, 12],
			"text-anchor": "bottom",
			"text-offset": [0, -0.8],
			"text-allow-overlap": false,
			visibility: visibleLayers.includes("transit-stations") ? "visible" : "none",
		},
		paint: {
			"text-color": "#303030",
			"text-halo-color": "#ffffff",
			"text-halo-width": 1.2,
		},
		minzoom: 13,
		metadata: { custom: true },
	})
}
